import dagre from '@dagrejs/dagre';
import { Edge, Node, Position } from '@xyflow/react';
import { ApprovalSetupCommonData } from 'settings/account/approvalSetup/__generated__/ApprovalSetupCommonData';
import { RequestorConditionsValues } from '../../types';
import { EdgeEnumValues } from '../Node/common/CustomeEdge';
import { ApprovalSetupFlowCommonData } from 'settings/account/intuitiveApprovalSetupFlow/__generated__/ApprovalSetupFlowCommonData';

type LayoutDirection = 'TB' | 'BT' | 'LR' | 'RL';

interface LayoutOptions {
  direction: LayoutDirection;
}

interface MeasuredNode extends Node {
  measured?: {
    width: number;
    height: number;
  };
}

export const getLayoutElements = (
  nodes: MeasuredNode[],
  edges: Edge[],
  options: LayoutOptions
): { nodes: Node[]; edges: Edge[] } => {
  const g = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({
    rankdir: options.direction,
    ranksep: 100,
  });

  // Add edges
  edges.forEach((edge) => g.setEdge(edge.source, edge.target));
  // Add nodes with measured width and height
  nodes.forEach((node) => {
    g.setNode(node.id, {
      width: 300,
      height: 111,
    });
  });

  dagre.layout(g);

  const layoutNodes: Node[] = nodes.map((node) => {
    const dagreNode = g.node(node.id);
    const width = 300;
    const height = 111;

    const position = {
      x: dagreNode.x - width / 2,
      y: dagreNode.y - height / 2,
    };

    return {
      ...node,
      position,
    };
  });

  return { nodes: layoutNodes, edges };
};

function areAllObjectValuesNull(obj: Record<string, any>): boolean {
  return Object.values(obj).every((value) => value === null);
}

type FlowGraph = {
  nodes: MeasuredNode[];
  edges: Edge[];
};

export const convertToReactFlowNodes = (
  conditions: RequestorConditionsValues[] | null,
  approvalTypes: ApprovalSetupFlowCommonData,
  flowLayoutState: 'horizontal' | 'vertical',
  disabled: boolean,
  isFlowUpdatable: boolean
): FlowGraph => {
  const nodes: MeasuredNode[] = [];
  const edges: Edge[] = [];

  const baseX = 0;
  const baseY = 100;
  // Optional: Title Node at top
  nodes.push({
    id: 'node-1',
    type: 'TitleNode',
    data: { label: 'Conditions' },
    targetPosition:
      flowLayoutState === 'horizontal' ? Position.Left : Position.Top,
    sourcePosition:
      flowLayoutState === 'horizontal' ? Position.Right : Position.Bottom,
    position: { x: baseX, y: 0 },
  });

  if (!!!conditions) return { edges: [], nodes: [] };

  // Iterate through conditions
  conditions.forEach((condition, index) => {
    const nodeId = `node-${index + 2}`; // +2 since node-1 is used
    const isPlaceholder = areAllObjectValuesNull(condition);

    nodes.push({
      id: nodeId,
      type: isPlaceholder ? 'AddConditionNode' : 'ConditionNode',
      data: {
        // label: isPlaceholder ? 'Box-2' : `Approval-${index}`, // or condition.description or importanceLevel
        ...condition,
        disabled: disabled,
        isFlowUpdatable: isFlowUpdatable,
      },
      className: '',
      targetPosition:
        flowLayoutState === 'horizontal' ? Position.Left : Position.Top,
      sourcePosition:
        flowLayoutState === 'horizontal' ? Position.Right : Position.Bottom,
      position: {
        x: baseX,
        y: baseY * (index + 1),
      },
    });

    // Step 3: Generate edges
    if (index === 0) {
      const isFirstNodePlaceholder = areAllObjectValuesNull(condition);

      edges.push({
        id: `node-1-${nodeId}`,
        source: 'node-1',
        target: nodeId,
        sourceHandle: 'a',
        type: 'custom-edge',
        animated: isFirstNodePlaceholder,
        className: '',
      });
    } else {
      const sourceId = `node-${index + 1}`;
      edges.push({
        id: `${sourceId}-${nodeId}`,
        source: sourceId,
        target: nodeId,
        type: 'custom-edge',
        className: '',
        label:
          index < conditions.length - 1
            ? conditions[index].isAndCondition
              ? EdgeEnumValues.and
              : EdgeEnumValues.or
            : undefined,
        animated: index === conditions.length - 1,
      });
    }
  });

  return { nodes, edges };
};
